import PropTypes from "prop-types";
import React from "react";
import DropDownEditor from "./DropDownEditor";
import PacksEditor from "./PacksEditor";
import EachesEditor from "./EachesEditor";
import InputEditor from "./InputEditor";
import ViewsToolPanel from "./ViewsToolPanel";
import { Icon, Popup } from "semantic-ui-react";
import { columns, statusCode } from "../../utilities/columnNames";
import _ from "lodash";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import {
  compose,
  setDisplayName,
  setPropTypes,
  withProps,
  withPropsOnChange,
  withStateHandlers,
  withState,
} from "recompose";

import { dropdowncellrenderer, invalidcell, warningcell } from "./DataGrid.css";

import { getMetadata } from "./util";
import { updateComputedColumns } from "../../utilities/computations";

/**
 * Datagrid is exported as a composed functional component. In this case it'd be easier to think
 * about it as a class and handle it's own internal state and actions, but this way we can make sure we
 * only create and update out inner *state* by changing our inputs. This component shouldn't change at
 * all unless there is a change in the inputs coming from ProductDetail.
 */
export default compose(
  withStateHandlers(
    /**
     * The first composition on Datagrid is in charge of exposing the current gridAPI to the rest of the
     * functions in the chain. For them it'll look as part of the props received, as gridAPI.
     * @param gridAPI
     * @returns {{gridAPI: *}}
     */
    ({ gridAPI }) => ({ gridAPI: gridAPI }),
    {
      onGridReady: () => (params) => ({ gridAPI: params.api }),
    }
  ),
  withProps(
    /**
     * In this composition we define the default column list for the grid, (before filtering by views), here
     * we define everythinf related to every field included in the grid, including validations, render rules,
     * etc. One of the most important fields defined for each column here is the cellEditor, which is the one
     * which allows each column to be defined edited by the user, including special cases like Packs and
     * Eaches editors.
     *
     * This composition uses the prop "dynamicMutableObjectForDataTableEditors", it's not a required field,
     * meaning this will still produce a list of columns even if that prop is undefined, it's still a critical
     * part of the process to have it, as many fields depend on values included in that prop.
     *
     * Here is also where we define how and when to show error messages inside the grid, by using cell styles, and
     * cell tooltips.
     *
     * Yhe definition of the status column is included in this composition, and a very similar one is also
     * specified on the AgGrid component props, as a fallback when that definition isn't part of the field itself.
     *
     * To show these errors we consider their sources and act accordingly. For nor we have 3 types of errors:
     *
     * Local errors: are errors produced by our own local validators, these are produced in the local cycle of
     * grid updates, right after any changes are made to the grid (dropdown values have been loaded, user changes
     * some field value, added a new row etc. Any change of input would trigger a reevaluation of the data in each
     * field, and possibly generating new local errors.
     *
     * Server errors: are errors related to each product, but send by the server, these errors get sent back
     * to the client after a "save" attempt, and they *must* specify the field they come from. This was we can
     * show the right cell as red, and add the right message on it's tooltip.
     *
     * Workflow errors: A workflow error is one simple message associated with a product, these errors don't have
     * any field associations at all, and as such we only show them on the Status column.
     *
     * All these errors are shown both ways, as a red cell background on the associated cells, plus the Status cell,
     * and as a tooltip specifying the reason of the error.
     *
     * @param dynamicMutableObjectForDataTableEditors
     * @returns {{columnDefs: *[]}}
     */
    ({ dynamicMutableObjectForDataTableEditors }) => {
      const isEditable =
        localStorage.getItem("isEditable") === "true" ? true : false;
      const numericCell = (col) => ({
        suppressKeyboardEvent: (evt) =>
          evt.editing && _.includes([37, 39], _.get(evt, "event.keyCode")),
        cellEditor: "InputEditor",
        cellEditorParams: {
          charRegex: /^\d+$/,
          handleInput: (e) => {
            if (
              !_.includes([8, 17, 37, 39], e.keyCode) &&
              /^[^\d]+$/.test(e.key) &&
              !e.ctrlKey
            ) {
              e.preventDefault();
            }
          },
          parseValue: (v) => {
            if (col === "styleSeq" && v.length === 0) return 1;
            else return _.defaultTo(v, "0");
          },
        },
        type: "numericColumn",
      });

      const moneyCell = () => ({
        suppressKeyboardEvent: (evt) =>
          evt.editing && _.includes([37, 39], _.get(evt, "event.keyCode")),
        valueFormatter: (params) =>
          _.chain(params)
            .get("value", "")
            .thru(
              _.cond([
                [_.isEmpty, _.constant("")],
                [_.stubTrue, (v) => `$${v}`],
              ])
            )
            .value(),
        cellEditor: "InputEditor",
        cellEditorParams: {
          charRegex: /^[\d.]+$/,
          handleInput: (e) => {
            if (
              !_.includes([8, 17, 37, 39], e.keyCode) &&
              /^[^\d.]+$/.test(e.key) &&
              !e.ctrlKey
            ) {
              e.preventDefault();
            }
          },
          parseValue: (v) =>
            _.chain(v)
              .defaultTo("0")
              .thru((v) => (/\./.test(v) ? v : `${v}.0`))
              .replace(/[^\d.]/, "")
              .split(".")
              .slice(0, 2)
              .map((v, i) =>
                i === 0
                  ? _.padStart(v, 2, "0")
                  : _.chain(v).padEnd(2, "0").truncate({ length: 2 }).value()
              )
              .join(".")
              .value(),
        },
        type: "numericColumn",
      });

      const dropDownCell = ({ values, optional }) => {
        const renderFn = (val) =>
          _.chain(values)
            .thru(
              _.cond([
                [_.isFunction, (v) => v(val)],
                [_.stubTrue, _.identity],
              ])
            )
            .map((v) => ({ ...v, value: _.toString(_.get(v, "value")) }))
            .find({ value: _.toString(_.get(val, "value", val)) })
            .get("text")
            .defaultTo(`( ${_.get(val, "value", "")} )`)
            .value();

        return {
          cellRenderer: renderFn,
          cellClass: dropdowncellrenderer,
          cellEditor: "DropDownEditor",
          suppressKeyboardEvent: (evt) =>
            evt.editing && _.includes([38, 40], _.get(evt, "event.keyCode")),
          cellEditorParams: {
            optional: optional,
            options: values,
          },
        };
      };

      return {
        columnDefs: [
          {
            field: "Status",
            headerName: "Status",
            width: 100,
            editable: false,
            cellClassRules: {
              [invalidcell]: (params) => {
                const localErrors = _.chain(params)
                  .get("data.metadata")
                  .values()
                  .flatMapDeep("errorMsgs")
                  .value();
                const serverErrors = _.chain(params)
                  .get("data.serverErrors")
                  .defaultTo([])
                  .value();
                const hasErrors = !_.isEmpty([...localErrors, ...serverErrors]);
                return hasErrors;
              },
              [warningcell]: (params) => {
                const localErrors = _.chain(params)
                  .get("data.metadata")
                  .values()
                  .flatMapDeep("errorMsgs")
                  .value();
                const serverErrors = _.chain(params)
                  .get("data.serverErrors")
                  .defaultTo([])
                  .value();
                const workflowError = _.get(
                  params,
                  "data.WorkflowErrorMessage"
                );

                const hasErrors = !_.isEmpty([...localErrors, ...serverErrors]);
                const hasWarnings =
                  _.isEqual(_.get(params, "value"), "Complete") &&
                  !_.isEmpty(workflowError);

                return hasErrors ? false : hasWarnings;
              },
            },
            tooltip: (params) => {
              const localErrors = _.chain(params)
                .get("data.metadata")
                .values()
                .filter((v) => _.get(v, "errorMsgs.length"))
                .flatMapDeep((v) => `${_.get(v, "errorMsgs")}`)
                .value();
              const serverErrors = _.chain(params)
                .get("data.serverErrors")
                .map("errorMessage")
                .defaultTo([])
                .value();
              const workflowError = _.get(params, "data.WorkflowErrorMessage");

              return _.chain([workflowError, ...localErrors, ...serverErrors])
                .uniq()
                .reject(_.isEmpty)
                .thru(
                  _.cond([
                    [_.negate(_.isEmpty), (v) => _.join(v, "\n")],
                    [_.stubTrue, _.constant(_.get(params, "value"))],
                  ])
                )
                .value();
            },
          },
          {
            field: "VendorPid",
            headerName: "Vend PID",
            width: 150,
            editable: isEditable,
          },
          {
            field: "AllocationNotes",
            headerName: "Allocation Notes",
            width: 150,
            suppressKeyboardEvent: (evt) =>
              evt.editing && _.includes([37, 39], _.get(evt, "event.keyCode")),
            cellEditor: "InputEditor",
            editable: isEditable,
            cellEditorParams: {
              handleInput: (e) => {
                const len = _.get(e, "target.value.length", 0);
                if (
                  !(
                    (/^[a-z0-9\s]+$/i.test(e.key) && !_.gt(len, 30)) ||
                    _.includes([8, 17, 37, 39], e.keyCode)
                  )
                ) {
                  e.preventDefault();
                }
              },
              parseValue: (v) => _.chain(v).trim().defaultTo("").value(),
            },
          },
          {
            field: "VendorPidDesc",
            headerName: "Vend PID Desc",
            width: 150,
            editable: isEditable,
          },
          {
            field: "MbksClass",
            headerName: "Class",
            width: 150,
            editable: isEditable,
            ...dropDownCell({
              values: _.get(dynamicMutableObjectForDataTableEditors, "classes"),
            }),
          },
          {
            field: "MbksSubClass",
            headerName: "SubCl",
            width: 150,
            editable: isEditable,
            ...dropDownCell({
              values: _.get(
                dynamicMutableObjectForDataTableEditors,
                "subclasses"
              ),
            }),
          },
          {
            headerName: "Eaches/Packs",
            width: 150,
            editable: (params) =>
              _.eq(_.get(params, "node.level"), 0) &&
              !_.get(params, "data.isVendorUpcMode"),
            editable: isEditable,
            ...dropDownCell({
              values: [
                { id: 0, value: "eaches", text: "Eaches" },
                { id: 1, value: "packs", text: "Packs" },
              ],
              optional: false,
            }),
            valueGetter: (params) => {
              return _.get(params, "data.EachesOrPacks");
            },
            valueSetter: (params) => {
              if (!_.isEqual(params.oldValue, params.newValue)) {
                _.set(params, "data.EachesOrPacks", params.newValue);

                if (
                  params.oldValue === "eaches" &&
                  params.newValue === "packs"
                ) {
                  _.set(params, "data.Quantity", 0);
                  _.set(params, "data.TotalPacks", 0);
                  _.set(params, "data.SizeDetails", []);
                  _.set(params, "data.PackDetails", []);
                }

                return true;
              } else {
                return false;
              }
            },
          },
          {
            field: "SizeGroupCode",
            headerName: "Size group",
            width: 150,
            editable: (params) =>
              _.eq(_.get(params, "node.level"), 0) &&
              !!_.get(params, "data.EachesOrPacks") &&
              !_.get(params, "data.isVendorUpcMode"),
            cellEditor: "DropDownEditor",
            editable: isEditable,
            ...dropDownCell({
              values: _.get(
                dynamicMutableObjectForDataTableEditors,
                "sizegroups"
              ),
              optional: true,
            }),
          },
          {
            headerName: "Pack/Size Ranges",
            width: 150,
            editable: (params) =>
              _.eq(_.get(params, "node.level"), 0) &&
              !_.get(params, "data.isVendorUpcMode"),
            editable: isEditable,
            cellRenderer: "agGroupCellRenderer",
            cellRendererParams: {
              suppressPadding: true,
              suppressDoubleClickExpand: true,
              suppressCount: true,
            },
            valueGetter: (params) => {
              return {
                sizes: _.get(params, "data.SizeDetails"),
                packs: _.get(params, "data.PackDetails"),
              };
            },
            valueSetter: (params) => {
              if (!_.isEqual(params.oldValue, params.newValue)) {
                _.set(
                  params,
                  "data.Quantity",
                  _.chain(params)
                    .get("newValue.sizes")
                    .map("Quantity")
                    .reduce((a, b) => a + _.toNumber(b), 0)
                    .value()
                );
                if (
                  _.get(params, "data.EachesOrPacks") === "eaches" &&
                  !!_.get(params, "data.SizeGroupCode")
                ) {
                  _.set(
                    params,
                    "data.TotalPacks",
                    _.chain(params)
                      .get("newValue.sizes")
                      .map("Quantity")
                      .reduce((a, b) => a + _.toNumber(b), 0)
                      .value()
                  );
                } else if (_.get(params, "data.EachesOrPacks") === "packs") {
                  _.set(
                    params,
                    "data.TotalPacks",
                    _.chain(params)
                      .get("newValue.packs")
                      .map("noOfPacks")
                      .reduce((a, b) => a + _.toNumber(b), 0)
                      .value()
                  );
                }
                _.set(
                  params,
                  "data.SizeDetails",
                  _.chain(params)
                    .get("newValue.sizes")
                    .filter((v) => v.Quantity)
                    .value()
                );
                _.set(
                  params,
                  "data.PackDetails",
                  _.get(params, "newValue.packs")
                );
                return true;
              } else {
                return false;
              }
            },
            valueFormatter: (params) => {
              if (
                _.get(params, "data.EachesOrPacks") === "packs" &&
                _.get(params, "node.level") === 0
              ) {
                return _.chain(params)
                  .get("value.packs")
                  .map(({ packName, noOfPacks, sizeScaleQtys }, i, a) =>
                    _.matches({ length: 1 })(a)
                      ? `${packName} (${_.chain(sizeScaleQtys)
                          .map("scaleQty")
                          .join("-")
                          .value()}) x${noOfPacks}`
                      : `${packName} x${noOfPacks}`
                  )
                  .join(", ")
                  .thru((v) => (_.isEmpty(v) ? " " : v))
                  .value();
              } else {
                return _.chain(params)
                  .get("value.sizes")
                  .map(({ NrfSizeName, Quantity }) => {
                    const name = _.truncate(NrfSizeName, {
                      length: 10,
                      omission: ".",
                    });

                    return _.get(params, "node.level") === 0
                      ? `${name} x${Quantity}`
                      : `${name}`;
                  })
                  .join(", ")
                  .thru((v) => (_.isEmpty(v) ? " " : v))
                  .value();
              }
            },
            tooltip: (params) => {
              if (
                _.get(params, "data.EachesOrPacks") === "packs" &&
                _.get(params, "node.level") === 0
              ) {
                return _.chain(params)
                  .get("value.packs")
                  .map(
                    ({ packName, noOfPacks, sizeScaleQtys }) =>
                      `${packName} ${
                        sizeScaleQtys
                          ? `(${_.chain(sizeScaleQtys)
                              .map("scaleQty")
                              .reduce((a, b) => `${a}-${b}`)
                              .value()})`
                          : ""
                      } x${noOfPacks}`
                  )
                  .join("\n")
                  .value();
              } else {
                return _.chain(params)
                  .get("value.sizes")
                  .map(
                    ({ NrfSizeName, Quantity }) => `${NrfSizeName} x${Quantity}`
                  )
                  .join("\n")
                  .value();
              }
            },
            cellEditor: null,
            cellEditorSelector: (params) => {
              return _.cond([
                [
                  _.matches({ EachesOrPacks: "packs" }),
                  _.constant({
                    component: "PacksEditor",
                    params: {
                      values: _.chain(dynamicMutableObjectForDataTableEditors)
                        .get("sizegroups")
                        .find({ value: _.get(params, "data.SizeGroupCode") })
                        .value(),
                    },
                  }),
                ],
                [
                  _.matches({ EachesOrPacks: "eaches" }),
                  _.constant({
                    component: "EachesEditor",
                    params: {
                      values: _.chain(dynamicMutableObjectForDataTableEditors)
                        .get("sizegroups")
                        .find({ value: _.get(params, "data.SizeGroupCode") })
                        .value(),
                    },
                  }),
                ],
              ])(_.get(params, "data"));
            },
            suppressKeyboardEvent: (evt) =>
              evt.editing &&
              _.includes([37, 38, 39, 40], _.get(evt, "event.keyCode")),
            // cellEditorParams: params => ({
            //     values: _.chain(params).get("node.data.sizeGroups").find({ value: _.get(params, "node.data.currentSizeGroup") }).value()
            // })
          },
          {
            field: "TotalPacks",
            headerName: "Total Packs/Units",
            editable: (params) =>
              _.eq(_.get(params, "node.level"), 0) &&
              !_.get(params, "data.isVendorUpcMode"),
            width: 150,
            type: "numericColumn",
            editable: false,
          },
          {
            field: "Quantity",
            editable: (params) =>
              _.eq(_.get(params, "node.level"), 0) &&
              _.get(params, "data.EachesOrPacks") === "eaches" &&
              !_.get(params, "data.SizeGroupCode"),
            editable: isEditable,
            headerName: "QTY",
            width: 150,
            type: "numericColumn",
            ...numericCell(),
          },
          {
            field: "InitialQuantity",
            editable: false,
            headerName: "Initial Qty",
            width: 150,
            type: "numericColumn",
            ...numericCell(),
          },
          {
            field: "Cost",
            headerName: "Cost",
            width: 80,
            editable: isEditable,
            ...moneyCell(),
          },
          {
            field: "TicketRetail",
            headerName: "Ticket Retail",
            width: 150,
            editable: isEditable,
            ...moneyCell(),
          },
          {
            field: "CompareAt",
            headerName: "Compare At",
            width: 150,
            editable: isEditable,
            ...moneyCell(),
          },
          {
            field: "CompareAtSource",
            headerName: "Compare At Source",
            width: 180,
            editable: isEditable,
            ...dropDownCell({
              values: _.get(
                dynamicMutableObjectForDataTableEditors,
                "compareatsources"
              ),
            }),
          },
          {
            field: "CompareAtNotes",
            headerName: "Compare At Notes",
            editable: isEditable,
            width: 180,
          },
          {
            field: "AndUp",
            headerName: "And Up",
            width: 80,
            editable: isEditable,
            ...dropDownCell({
              values: [
                { value: "Y", text: "Y" },
                { value: "N", text: "N" },
              ],
            }),
          },
          {
            field: "Source",
            headerName: "Source",
            editable: isEditable,
            ...dropDownCell({
              values: [
                { id: 0, value: 1, text: "1-Pack", title: "1-Pack" },
                { id: 1, value: 3, text: "3-CRC", title: "3-CRC" },
                { id: 2, value: 5, text: "5-Cut", title: "5-Cut" },
                { id: 3, value: 7, text: "7-Oppty", title: "7-Oppty" },
                { id: 4, value: 9, text: "9-Jobber", title: "9-Jobber" },
              ],
            }),
          },
          {
            field: "Season",
            headerName: "Season",
            editable: isEditable,
            ...dropDownCell({
              values: [
                { id: 0, value: 0, text: "0-Basics", title: "Basics" },
                { id: 1, value: 1, text: "1-Spring", title: "1-Spring" },
                { id: 2, value: 2, text: "2-Fall", title: "2-Fall" },
              ],
            }),
          },
          {
            field: "MultiOf",
            headerName: "Multi-Of",
            editable: isEditable,
            ...numericCell(),
          },
          {
            field: "MasterStyleDiv",
            headerName: "Div/MSTSTY",
            width: 140,
            editable: isEditable,
            ...numericCell(),
          },
          {
            field: "MasterStyle",
            headerName: "MSTSTY #",
            width: 130,
            editable: isEditable,
            ...numericCell(),
          },
          {
            field: "MasterStyleName",
            headerName: "MSTSTY Desc",
            editable: false,
            width: 180,
          },
          {
            field: "NrfColor",
            headerName: "NRF Color",
            width: 150,
            editable: isEditable,
            ...dropDownCell({
              values: _.get(
                dynamicMutableObjectForDataTableEditors,
                "nfrcolors"
              ),
            }),
          },
          {
            field: "VndColorName",
            headerName: "Vendor Color",
            editable: isEditable,
          },
          {
            field: "NrfSize",
            headerName: "NRF Size",
            editable: isEditable,
            ...numericCell(),
          },
          {
            field: "NrfSizeName",
            headerName: "Size Desc",
            editable: isEditable,
          },
          {
            field: "MbksStyleSeq",
            headerName: "Style Seq",
            editable: isEditable,
            ...numericCell("styleSeq"),
          },
          {
            field: "MbksMarkStyleNo",
            headerName: "MKST",
            editable: false,
            width: 180,
          },
          {
            field: "MbksMarkStyleName",
            headerName: "MKST Desc",
            editable: isEditable,
            width: 180,
          },
          {
            field: "VendorUpc",
            headerName: "Vendor UPC",
            width: 150,
            editable: (params) =>
              _.eq(_.get(params, "node.level"), 0) &&
              _.cond([
                [() => _.get(params, "data.isVendorUpcMode"), _.constant(true)],
                [
                  _.stubTrue,
                  () =>
                    _.chain(dynamicMutableObjectForDataTableEditors)
                      .get("sizegroups", [])
                      .find({ value: _.get(params, "data.SizeGroupCode") })
                      .thru((v) => !v)
                      .value(),
                ],
              ])(),
            editable: isEditable,
            ...numericCell(),
          },
          {
            field: "MbksPid",
            headerName: "PID",
            editable: false,
            width: 150,
          },
          {
            field: "MbksPidDesc",
            headerName: "PID Desc",
            editable: false,
            width: 150,
          },
          {
            field: "CorpInHouseUpc",
            editable: false,
            headerName: "CIH",
            width: 180,
          },
          {
            field: "MBKSExtCost",
            headerName: "Ext Cost",
            editable: false,
            width: 150,
            ...moneyCell(),
          },
          {
            field: "MBKSExtRetail",
            headerName: "Ext Retail",
            editable: false,
            width: 150,
            ...moneyCell(),
          },
          {
            field: "MBKSMU",
            headerName: "MU%",
            editable: false,
            width: 150,
          },
          {
            field: "Delete",
            headerName: "Delete",
            editable: false,
            width: 80,
            tooltip: () => "Delete Product",
            cellRenderer: "deleteBtn",
          },
        ],
      };
    }
  ),
  withState("currentView", "setCurrentView", null),
  withPropsOnChange(
    ["columnDefs", "currentView"],
    /**
     * this is the next composition in the chain having to do with the definition of the column list, and it makes
     * sure the grid only sees the definition for the columns included in th currently selected view, and in the
     * right order. Meaning every time the user changes the currently selected view, they will trigger this
     * composition (not the *onChange* part of the composition).
     *
     * What we do here is take the default definition, and filter out those columns that shouldn't be shown for
     * the currently selected view, and sort them later in the order specified in the view.
     *
     * we do not remove the fields from the new column definition though, we merely "hide" them, meaning the user can
     * still see them in the column controller, but they are to the end of the column list, and unchecked.
     *
     * @param props
     * @returns {{viewColumnDefs: *}}
     */
    (props) => {
      let defCols = _.chain(props)
        .get("columnDefs")
        .map((e) => {
          return { ...e, fieldName: e.field || e.headerName };
        })
        .value();
      let currentViewCols = _.chain(props)
        .get("currentView.cols")
        .sortBy("pos")
        .thru(
          _.cond([
            [_.isEmpty, () => defCols],
            [_.stubTrue, _.identity],
          ])
        )
        .value();

      return {
        viewColumnDefs: _.chain(defCols)
          .map((e) => {
            return {
              ...e,
              hide: _.chain(currentViewCols)
                .find({ fieldName: e.fieldName })
                .thru((e) => !e)
                .value(),
              pos: _.chain(currentViewCols)
                .find({ fieldName: e.fieldName })
                .get("pos")
                .value(),
            };
          })
          .sortBy("pos")
          .map((e) => _.omit(e, ["pos", "fieldName"]))
          .thru(
            _.cond([
              [_.isEmpty, () => defCols],
              [_.stubTrue, _.identity],
            ])
          )
          .value(),
      };
    }
  ),
  withPropsOnChange(
    ["rows", "dynamicMutableObjectForDataTableEditors", "isVendorUpcMode"],
    /**
     * this composition has to do with sizes and sub-rows, and it's here where we define the tree view for
     * sub sizes so that the user can see a detail of the quantities for each size specified by Packs or
     * Eaches on each main row.
     * @param props
     */
    (props) => {
      //update tree Data with pack's subRows
      const rowsData = _.chain(props)
        .get("rows")
        .flatMapDeep((r, i, a) => {
          const rootName = _.chain(a)
            .filter({ ImportRowNo: _.get(r, "ImportRowNo") })
            .thru((v) =>
              _.matches({ length: 1 })(v)
                ? _.get(r, "ImportRowNo")
                : `${_.get(r, "ImportRowNo")} (${_.chain(v)
                    .findIndex(r)
                    .defaultTo(0)
                    .thru((v) => v + 1)
                    .value()})`
            )
            .value();
          return [
            {
              ...r,
              isVendorUpcMode: _.get(props, "isVendorUpcMode", false),
              treePath: [rootName],
            },

            _.chain(r)
              .get("SizeDetails")
              .map((subrow, i) => {
                return _.chain(subrow).get("Quantity").toNumber().eq(0).value()
                  ? null
                  : {
                      ...r,
                      ...subrow,
                      ProductId: `${_.get(r, "ProductId")}/${_.get(
                        subrow,
                        "NrfSize"
                      )}${i}`,
                      ImportRowNo: `${_.get(r, "ImportRowNo")}/${_.get(
                        subrow,
                        "NrfSize"
                      )}${i}`,
                      treePath: [rootName, _.get(subrow, "NrfSize")],
                      SizeDetails: [{ ...subrow }],
                      TotalPacks: _.get(subrow, "Quantity"),
                    };
              })
              .compact()
              .value(),
          ];
        })
        .map(updateComputedColumns)
        .compact()
        .value();

      _.invoke(
        props,
        "gridAPI.setRowData",
        _.zipWith(rowsData, getMetadata(rowsData), (a, b) => {
          return {
            ...a,
            metadata: _.get(a, "treePath.length") === 1 ? b : {},
          };
        })
      );
    }
  ),
  setPropTypes({
    rows: PropTypes.array,
    rowsMetadata: PropTypes.array,
    onRowsUpdated: PropTypes.func.isRequired,
    onClickAddNewRow: PropTypes.func.isRequired,
    isVendorUpcMode: PropTypes.bool.isRequired,
    worksheetType: PropTypes.string.isRequired,
  }),
  setDisplayName("AGDataGrid")
)(
  ({
    onGridReady,
    columnDefs,
    gridAPI,
    viewColumnDefs,
    rowData,
    dynamicMutableObjectForDataTableEditors,
    onRowsUpdated,
    onClickAddNewRow,
    deleteProduct,
    setCurrentView,
    worksheetType,
  }) => {
    let previousRowIndex = 0;
    let currentHeaderName = "";
    let previousHeaderName = "";

    return (
      <div
        className="ag-theme-balham"
        style={{
          overflow: "hidden",
          minHeight: "500px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AgGridReact
          columnDefs={viewColumnDefs}
          defaultColDef={{
            editable: (params) => _.eq(_.get(params, "node.level"), 0),
            cellEditor: "agTextCellEditor",
            cellClassRules: {
              [invalidcell]: (params) => {
                const fieldName = _.get(params, "colDef.field");
                const localErrors = _.get(
                  params,
                  `data.metadata.${fieldName}.errorMsgs`,
                  []
                );
                const serverErrors = _.chain(params)
                  .get("data.serverErrors")
                  .filter({ fieldName: fieldName })
                  .defaultTo([])
                  .value();
                return !_.isEmpty([...localErrors, ...serverErrors]);
              },
            },
            tooltip: (params) => {
              const fieldName = _.get(params, "colDef.field");
              const localErrors = _.chain(params)
                .get(`data.metadata.${fieldName}.errorMsgs`, [])
                .map((v) => `${v}`)
                .value();
              const serverErrors = _.chain(params)
                .get("data.serverErrors")
                .filter({ fieldName: fieldName })
                .map("errorMessage")
                .defaultTo([])
                .value();
              return _.chain([...localErrors, ...serverErrors])
                .thru(
                  _.cond([
                    [_.negate(_.isEmpty), (v) => _.join(v, "\n")],
                    [_.stubTrue, _.constant(_.get(params, "value"))],
                  ])
                )
                .value();
            },
          }}
          treeData={true}
          rememberGroupStateWhenNewData={true}
          getDataPath={(data) => {
            return _.get(data, "treePath");
          }}
          getRowStyle={(params) => {
            if (params.node.level > 0) {
              return { background: "rgba(217, 231, 246, 0.43)" };
            }
          }}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                },
              },
              {
                id: "views",
                labelDefault: "views",
                labelKey: "views",
                iconKey: "columns",
                toolPanel: "ViewsToolPanel",
              },
            ],
          }}
          reactNext={true}
          onGridReady={onGridReady}
          processCellForClipboard={(params) => {
            const headerName = _.chain(params)
              .get("column.colDef.headerName")
              .value();
            currentHeaderName = headerName;
            if (
              headerName === "Pack/Size Ranges" ||
              headerName === "Eaches/Packs" ||
              headerName === "Size group"
            ) {
              const eq = _.curry(_.eq);
              previousRowIndex = params.node.data.ImportRowNo;
              return _.chain(params)
                .get("column.colDef.headerName")
                .thru(
                  _.cond([
                    [
                      eq("Pack/Size Ranges"),
                      () =>
                        JSON.stringify({
                          ..._.get(params, "value"),
                          SizeGroupCode: _.get(
                            params,
                            "node.data.SizeGroupCode"
                          ),
                        }),
                    ],
                    [_.stubTrue, () => _.get(params, "value")],
                  ])
                )
                .value();
            }
            return _.get(params, "value");
          }}
          processCellFromClipboard={(params) => {
            const headerName = _.chain(params)
              .get("column.colDef.headerName")
              .value();
            previousHeaderName = headerName;
            if (
              headerName === "Pack/Size Ranges" ||
              headerName === "Eaches/Packs" ||
              headerName === "Size group"
            ) {
              const oldRow = params.api.getRowNode(previousRowIndex);
              const rowNode = params.api.getRowNode(
                params.node.data.ImportRowNo
              );
              let newData = params.node.data;
              let sizeGroupCode = !oldRow.data.SizeGroupCode
                ? ""
                : parseInt(oldRow.data.SizeGroupCode);
              newData.EachesOrPacks = oldRow.data.EachesOrPacks;
              newData.SizeGroupCode = sizeGroupCode;
              newData.PackDetails = oldRow.data.PackDetails;
              newData.SizeDetails = oldRow.data.SizeDetails;
              newData.TotalPacks = oldRow.data.TotalPacks;
              newData.Quantity = oldRow.data.Quantity;
              rowNode.setData(newData);
              rowNode.setDataValue("SizeGroupCode", sizeGroupCode);
              const eq = _.curry(_.eq);
              return _.chain(params)
                .get("column.colDef.headerName")
                .thru(
                  _.cond([
                    [
                      eq("Pack/Size Ranges"),
                      () => JSON.parse(_.get(params, "value")),
                    ],
                    [
                      eq("Size group"),
                      () => {
                        if (_.get(params, "value") != "")
                          return _.toNumber(_.get(params, "value"));
                      },
                    ],
                    [_.stubTrue, () => _.get(params, "value")],
                  ])
                )
                .value();
            } else if (previousHeaderName === currentHeaderName) {
              return _.get(params, "value");
            }
          }}
          processDataFromClipboard={(params) => {
            return params.data;
          }}
          enableRangeSelection={true}
          enableColResize={true}
          rowHeight={25}
          animateRows={true}
          enableFilter={true}
          enableSorting={true}
          deltaRowDataMode={true}
          getRowNodeId={(data) => _.get(data, "ImportRowNo")}
          onCellValueChanged={onRowsUpdated}
          getContextMenuItems={(params) => [
            ..._.get(params, "defaultItems", []),
            {
              name: "Delete Product",
              action: () =>
                deleteProduct(_.get(params, "node.data.ImportRowNo")),
              disabled: _.eq(
                _.get(params, "node.data.StatusCode"),
                statusCode.Complete
              ),
            },
          ]}
          onRowDataUpdated={(params) => {
            _.invoke(params, "api.refreshCells", { force: true });
          }}
          groupSuppressAutoColumn={true}
          frameworkComponents={{
            deleteBtn: (props) => <div>{props.value}</div>,
            InputEditor: InputEditor,
            DropDownEditor: DropDownEditor,
            PacksEditor: PacksEditor,
            EachesEditor: EachesEditor,
            ViewsToolPanel: (props) =>
              new ViewsToolPanel({ ...props, setCurrentView, worksheetType }),
          }}
          aggFuncs={{
            sum: (values) =>
              _.chain(values)
                .reduce(
                  (a, b) => a + (/^[0-9.]+$/.test(b) ? _.toNumber(b) : 0),
                  0
                )
                .round(2)
                .value(),
            first: (values) => _.first(values),
          }}
          postProcessPopup={(params) => {
            const cellPos = _.cond([
              [
                _.matches({ type: "contextMenu" }),
                (p) => ({
                  top: _.get(p, "mouseEvent.y"),
                  left: _.get(p, "mouseEvent.x"),
                }),
              ],
              [
                _.stubTrue,
                (p) =>
                  _.chain(p)
                    .invoke("eventSource.getClientRects")
                    .first()
                    .value(),
              ],
            ])(params);
            const windowOffsets = {
              left: window.pageXOffset || document.documentElement.scrollLeft,
              top: window.pageYOffset || document.documentElement.scrollTop,
            };
            const newStyle = `top: ${
              cellPos.top + windowOffsets.top
            }px; left: ${cellPos.left + windowOffsets.left}px`;
            params.ePopup.setAttribute("style", newStyle);
          }}
        ></AgGridReact>
      </div>
    );
  }
);

// Notice AndUp is not included because, is just Y/N values
// and does not need custom format
const columnsWithDropdowns = [
  "MbksClass",
  "MbksSubClass",
  "Source",
  "Season",
  "TicketType",
  "NrfColor",
  "CompareAtSource",
];

const cellStyleWithErrors = {
  color: "#db2828",
};

const iconStyle = {
  position: "absolute",
  top: -4,
  left: -4,
  zIndex: 1000,
};

function columnsWithErrors(columns) {
  return columns.map((col) => {
    return {
      ...col,
      getRowMetaData: getRowMetaData,
      formatter: ColumnFormatter,
    };
  });
}

function ColumnFormatter({
  value: rawValue,
  dependentValues: { row, column },
}) {
  let popupContent;
  let value = rawValue;

  // format dropdown values
  if (~columnsWithDropdowns.indexOf(column.key)) {
    const options = column.editor.props.options();
    if (Array.isArray(options) && options.length) {
      const item = options.find((opt) => String(opt.value) === String(value));
      if (item) {
        value = String(item.text);
      }
    }

    if (value !== "" && value === rawValue) {
      const possibleDescription =
        row[column.key + "Name"] || row[column.key + "Desc"];
      if (possibleDescription !== void 0) {
        value = value + "-" + possibleDescription;
      }
    }
  }

  // The Status column changes its value and popup content if
  // the row comntains server errors
  if (column.key === "Status") {
    if (row.serverErrors && row.serverErrors.length) {
      popupContent = row.serverErrors.reduce(
        (acc, item) => (acc += item.errorMessage + ".<br /> "),
        ""
      );
      value = "With Errors!";
    }
  } else {
    // using dirty columns maintained in ./CustomEditor.js
    // Also see ./util.js
    if (
      row.dirtyColumns &&
      row.dirtyColumns.find((name) => name === column.key)
    ) {
      popupContent = getClientSideRowError(row, column);
    } else {
      if (rowHasServerSideErrors(row, column)) {
        popupContent = getServerSideRowError(row, column);
      } else {
        popupContent = getClientSideRowError(row, column);
      }
    }
  }

  if (popupContent) {
    return (
      <Popup
        inverted
        trigger={
          <div style={cellStyleWithErrors}>
            <Icon name="stop" size="tiny" style={iconStyle} disabled />
            {value}&nbsp;
          </div>
        }
      >
        <Popup.Content>
          <div dangerouslySetInnerHTML={{ __html: popupContent }} />
        </Popup.Content>
      </Popup>
    );
  } else {
    return value;
  }
}

function rowHasServerSideErrors(row, column) {
  return (
    !!row.serverErrors &&
    row.serverErrors.length &&
    row.serverErrors.find((item) => item.fieldName === column.key)
  );
}

function getClientSideRowError(row, column) {
  if (
    row.metadata &&
    row.metadata[column.key] &&
    row.metadata[column.key].hasErrors()
  ) {
    const firstMsg = row.metadata[column.key].errorMsgs[0];
    const colDictItem = columns.find((c) => c.value === column.key);
    const colDisplayName = colDictItem ? colDictItem.text : column.key;
    return colDisplayName + " " + firstMsg;
  }
}

function getServerSideRowError(row, column) {
  return row.serverErrors.find((item) => item.fieldName === column.key)
    .errorMessage;
}

function getRowMetaData(row, column) {
  return { row, column };
}



// WEBPACK FOOTER //
// ./components/product-detail/DataGrid.js